import { Search } from '@mui/icons-material';
import { Button, Checkbox, Divider, Skeleton, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import ModalV2 from '/@/components/bayer/modal-v2';
import ChipLabel from '/@/components/chip-label';
import { useMyFields } from '/@/hooks/fields';
import { _ } from '/@/locale';
import { getSessionCountry } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { Field } from '/@/types/field';
import { getCrops } from '/@/views/dashboard/hooks/crops';

const FieldSelectorModal = ({
    text,
    field,
    userNodeId,
    ModalV2Props,
    action,
}: {
    text: string;
    field?: Field;
    userNodeId: number;
    ModalV2Props: React.ComponentProps<typeof ModalV2>;
    action: {
        onClick: (fieldIds: number[]) => void;
        text: string;
    };
}) => {
    const [search, setSearch] = useState('');
    const [selectedFieldIdsSet, setSelectedFieldIdsSet] = useState<Set<number>>(new Set());
    const country = useSelector(getSessionCountry);
    const [searchParams, setSearchParams] = useSearchParams();
    const hasProcessedFieldIds = useRef(false);

    // Pre-select field from URL params (when returning from field creation)
    const fieldIdsFromUrl = searchParams.get('fieldIds');
    useEffect(() => {
        if (fieldIdsFromUrl && ModalV2Props.open && !hasProcessedFieldIds.current) {
            hasProcessedFieldIds.current = true;
            const fieldIds = fieldIdsFromUrl.split(',').map(Number).filter(Boolean);
            if (fieldIds.length > 0) {
                setSelectedFieldIdsSet(new Set(fieldIds));
                // Clear the fieldIds param to avoid re-selecting on subsequent opens
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('fieldIds');
                setSearchParams(newParams, { replace: true });
            }
        }
    }, [fieldIdsFromUrl, ModalV2Props.open, searchParams, setSearchParams]);

    const fieldsQuery = useMyFields({
        page: 1,
        pageSize: 10000,
        name: search,
        country: country.code,
        selectOnly: 'id,name,location,active_crop_summary',
        userNodeId,
        includeChildren: false,
    });

    const crops = getCrops({ size: 'xxs' });

    useEffect(() => {
        if (field && ModalV2Props.open) {
            setSelectedFieldIdsSet(new Set([field.id]));
        }
    }, [field, ModalV2Props.open]);

    const toggleFieldSelection = (fieldId: number) => {
        setSelectedFieldIdsSet((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(fieldId)) {
                newSet.delete(fieldId);
            } else {
                newSet.add(fieldId);
                mpTrackEvent('Visit report field selected', {});
            }
            return newSet;
        });
    };

    return (
        <ModalV2
            actions={
                <Stack direction='row' spacing={1} justifyContent='flex-end' sx={{ width: '100%' }}>
                    <Button
                        variant='contained'
                        color='secondary'
                        disabled={selectedFieldIdsSet.size === 0}
                        onClick={() => {
                            action.onClick(Array.from(selectedFieldIdsSet));
                        }}
                    >
                        {action.text}
                    </Button>
                </Stack>
            }
            {...ModalV2Props}
        >
            <Stack spacing={2} width='100%'>
                <Typography variant='subtitle1' color='primary' textAlign='left'>
                    {text}
                </Typography>
                <TextField
                    InputProps={{
                        startAdornment: <Search />,
                    }}
                    placeholder={_('Busca lotes por nombre')}
                    onChange={(e) => setSearch(e.target.value)}
                    value={search}
                    fullWidth
                />
                <Stack className='mp-mask' divider={<Divider />} width='100%'>
                    {fieldsQuery.isValidating ? (
                        <Stack direction='column' spacing={1}>
                            {Array.from({ length: 5 }, (_, index) => (
                                <Skeleton variant='rectangular' height={50} key={`skeleton-visit-${index}`} />
                            ))}
                        </Stack>
                    ) : (
                        fieldsQuery.data?.results?.map((f: Field) => {
                            const cropName = crops[f.activeCropMonitoringCrop || 'fallow']?.name;
                            const cropIcon = crops[f.activeCropMonitoringCrop || 'fallow']?.icon;
                            return (
                                <Stack
                                    direction='row'
                                    key={`field-${f.id}`}
                                    onClick={() => toggleFieldSelection(f.id)}
                                    sx={[
                                        {
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: 'action.hover',
                                            },
                                        },
                                        selectedFieldIdsSet.has(f.id) && {
                                            backgroundColor: 'action.selected',
                                        },
                                    ]}
                                >
                                    <Checkbox
                                        color='secondary'
                                        value={f.id}
                                        checked={selectedFieldIdsSet.has(f.id)}
                                        readOnly
                                    />
                                    <Stack
                                        spacing={0.5}
                                        px={1.5}
                                        py={1}
                                        justifyContent='flex-start'
                                        direction='column'
                                        alignItems='start'
                                    >
                                        <Stack
                                            direction='row'
                                            spacing={1}
                                            useFlexGap
                                            flexWrap='wrap'
                                            alignItems='center'
                                        >
                                            <Typography variant='subtitle2' color='primary' sx={{ textAlign: 'left' }}>
                                                {f.name}
                                            </Typography>
                                            <ChipLabel
                                                label={`${cropName} ${f.activeCropMonitoringProductName || ''}`}
                                                type={f.activeCropMonitoringCrop || 'fallow'}
                                                icon={cropIcon}
                                                size='xs'
                                            />
                                        </Stack>
                                        {f.location && (
                                            <Typography variant='caption' color='primary.mui.inactive'>
                                                {f.location}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Stack>
                            );
                        })
                    )}
                </Stack>
            </Stack>
        </ModalV2>
    );
};

export default FieldSelectorModal;
